<template>
    <div class="onboarding-page">
        <!-- SEO 结构化数据 -->
        <script type="application/ld+json" v-html="structuredData"></script>

        <!-- 顶部导航栏 -->
        <div class="top-nav">
            <div class="logo" @click="goHome">
                <span class="logo-icon">🛠️</span>
                <span class="logo-text">免费工具箱</span>
            </div>

            <!-- 搜索区域 -->
            <div class="search-container">
                <el-autocomplete
                    v-model="searchKeyword"
                    :fetch-suggestions="querySearch"
                    placeholder="搜索工具名称或功能..."
                    class="search-input"
                    :trigger-on-focus="false"
                    clearable
                    @select="handleSearchSelect"
                    @change="handleSearchChange"
                    prefix-icon="el-icon-search"
                >
                    <template slot-scope="{ item }">
                        <div class="search-suggestion">
                            <i :class="item.icon"></i>
                            <span class="suggestion-name">{{ item.name }}</span>
                            <span class="suggestion-tag">{{ item.tag }}</span>
                        </div>
                    </template>
                    <el-button slot="append" icon="el-icon-search" @click="handleSearchChange"></el-button>
                </el-autocomplete>

                <!-- 搜索历史下拉 -->
                <div class="search-history" v-if="showSearchHistory && searchHistory.length > 0">
                    <div class="history-header">
                        <span>搜索历史</span>
                        <el-button type="text" size="mini" @click="clearHistory">清除</el-button>
                    </div>
                    <div class="history-list">
                        <el-tag
                            v-for="(history, index) in searchHistory.slice(0, 5)"
                            :key="index"
                            class="history-item"
                            @click="searchFromHistory(history)"
                        >
                            {{ history }}
                        </el-tag>
                    </div>
                </div>
            </div>

            <!-- 收藏入口 -->
            <el-badge :value="favorites.length" :hidden="favorites.length === 0" class="favorite-badge">
                <el-button icon="el-icon-star-off" @click="showFavorites = !showFavorites">
                    收藏工具
                </el-button>
            </el-badge>
            <el-button type="primary" @click="showLoginAlert" class="login-button">登录</el-button>
        </div>

        <!-- 收藏工具栏 -->
        <transition name="el-zoom-in-top">
            <div class="favorites-bar" v-if="showFavorites && favorites.length > 0">
                <div class="favorites-header">
                    <span>我的收藏</span>
                    <el-button type="text" size="mini" @click="showFavorites = false">收起</el-button>
                </div>
                <div class="favorites-list">
                    <div
                        v-for="tool in favorites"
                        :key="tool.name"
                        class="favorite-item"
                        @click="handleToolClick(tool)"
                    >
                        <span :style="{ backgroundColor: tool.color }" class="favorite-icon">
                            {{ tool.icon }}
                        </span>
                        <span class="favorite-name">{{ tool.name }}</span>
                        <i class="el-icon-close remove-favorite" @click.stop="removeFavorite(tool)"></i>
                    </div>
                </div>
            </div>
        </transition>

        <!-- 主要内容区域 -->
        <div class="main-content">
            <!-- 热门搜索标签 -->
<!--            <div class="hot-search-bar" v-if="!searchKeyword">
                <span class="hot-label">🔥 热门搜索：</span>
                <el-tag
                    v-for="(keyword, index) in hotSearchKeywords"
                    :key="index"
                    class="hot-tag"
                    :type="index < 3 ? 'danger' : 'info'"
                    @click="searchFromHot(keyword)"
                >
                    {{ keyword }}
                </el-tag>
            </div>-->

            <!-- 欢迎区域 -->
            <div class="welcome-section" v-if="!searchKeyword && !showFavorites">
                <h1 class="welcome-title">发现高效工具，提升工作效率</h1>
                <p class="welcome-subtitle">免费无广告在线工具箱，包含文本处理、图片压缩、开发工具、格式转换、日常计算等工具，所有功能浏览器本地运行，保护隐私。</p>
                <el-button type="primary" class="explore-button" @click="scrollToTools">探索全部工具</el-button>
            </div>

            <!-- 搜索结果提示 -->
            <div class="search-result-tip" v-if="searchKeyword">
                <span>找到 {{ displayedTools.length }} 个相关工具</span>
                <el-button type="text" @click="clearSearch">清除搜索</el-button>
            </div>

            <!-- 收藏提示 -->
            <div class="search-result-tip" v-if="showFavorites && favorites.length > 0">
                <span>我的收藏 ({{ favorites.length }})</span>
            </div>

            <!-- 分类导航 -->
            <div class="categories" id="tools-section">
                <div class="category-tabs">
                    <div
                        v-for="category in categories"
                        :key="category.name"
                        class="category-tab"
                        :class="{ active: activeCategory === category.name }"
                        @click="handleCategoryClick(category.name)"
                    >
                        <span class="tab-icon">{{ category.icon }}</span>
                        <span class="tab-name">{{ category.label }}</span>
                    </div>
                </div>
            </div>

            <!-- 工具网格 -->
            <div class="tools-grid">
                <el-row :gutter="20">
                    <el-col :span="6" v-for="(tool, index) in displayedTools" :key="index">
                        <el-card shadow="hover" class="tool-card" @click.native="handleToolClick(tool)">
                            <div class="tool-icon">
                                <div class="icon-placeholder" :style="{ backgroundColor: tool.color }">
                                    {{ tool.icon }}
                                </div>
                                <!-- 收藏按钮 -->
                                <span
                                    class="favorite-btn"
                                    :class="{ 'is-favorited': isFavorited(tool) }"
                                    @click.stop="toggleFavorite(tool)"
                                >
                                    <i :class="isFavorited(tool) ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                                </span>
                            </div>
                            <div class="tool-info">
                                <div class="tool-name">{{ tool.name }}</div>
                                <div class="tool-description">{{ tool.description }}</div>
                                <div class="tool-stats">
                                    <span class="use-count">👣 {{ formatCount(tool.baseCount) }} 次</span>
                                    <el-tag size="mini" :type="tool.tagType">{{ tool.tag }}</el-tag>
                                </div>
                            </div>
                        </el-card>
                    </el-col>
                </el-row>

                <!-- 无结果提示 -->
                <el-empty
                    v-if="displayedTools.length === 0"
                    description="没有找到相关工具，试试其他关键词吧~"
                ></el-empty>
            </div>

            <!-- 空收藏提示 -->
            <div class="empty-favorites" v-if="showFavorites && favorites.length === 0">
                <el-empty description="暂无收藏工具，点击工具卡片上的⭐收藏">
                    <el-button type="primary" @click="showFavorites = false">去看看工具</el-button>
                </el-empty>
            </div>
        </div>

        <!-- 页脚 -->
        <div class="footer">
            <div class="footer-content">
                <div class="footer-links">
                    <a @click="showAboutUs = true">关于我们</a>
                    <a @click="showContactUs = true">联系我们</a>
                    <a @click="showHelpCenter = true">帮助中心</a>
                    <a @click="showUserAgreement = true">用户协议</a>
                </div>
                <div class="footer-gongyi">
                    <strong>公益链接</strong>：
                    <a href="https://gongyi.qq.com" target="_blank">腾讯公益</a> ·
                    <a href="https://www.redcross.org.cn" target="_blank">中国红十字会</a> ·
                    <a href="https://love.alipay.com" target="_blank">支付宝公益</a> ·
                    <a href="https://www.crcf.org.cn" target="_blank">中国红十字基金会</a>
                </div>
                <div class="copyright">
                    © 2023 - 2025 工具箱. 保留所有权利.
                </div>
                <div class="beian">
                    <a
                        href="https://beian.miit.gov.cn"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="beian-link"
                    >
                        京ICP备2025141589号
                    </a>
                </div>
            </div>
        </div>

        <!-- 关于我们弹窗 -->
        <el-dialog title="关于我们" :visible.sync="showAboutUs" width="600px">
            <div class="dialog-content">
                <h3>FreeToolBox 免费在线工具箱</h3>
                <p>FreeToolBox 是一个专注于提供免费、实用在线工具的平台。我们致力于为用户提供便捷、高效的工具服务，帮助您快速完成日常工作和学习任务。</p>
                <h4>我们的特点</h4>
                <ul>
                    <li>完全免费：无任何隐藏费用</li>
                    <li>本地处理：数据在浏览器本地处理，保护隐私安全</li>
                    <li>无需注册：直接使用，无需繁琐的注册登录流程</li>
                    <li>持续更新：不断添加新的实用工具</li>
                </ul>
                <p class="contact-tip">如果您有任何建议或问题，欢迎通过下方的"联系我们"功能与我们交流。</p>
            </div>
        </el-dialog>

        <!-- 联系我们弹窗 -->
        <el-dialog title="联系我们" :visible.sync="showContactUs" width="500px">
            <div class="dialog-content">
                <p>我们非常重视您的反馈和建议！</p>
                <div class="contact-methods">
                    <div class="contact-item">
                        <i class="el-icon-message"></i>
                        <span>邮箱：freetoolbox@163.com</span>
                    </div>
                    <div class="contact-item">
                        <i class="el-icon-chat-dot-round"></i>
                        <span>反馈：通过网站留言功能提交</span>
                    </div>
                </div>
                <p class="contact-tip">我们会在1-3个工作日内回复您的消息。</p>
            </div>
        </el-dialog>

        <!-- 帮助中心弹窗 -->
        <el-dialog title="帮助中心" :visible.sync="showHelpCenter" width="600px">
            <div class="dialog-content">
                <h4>常见问题</h4>
                <div class="faq-item">
                    <p class="faq-q">Q: 这些工具收费吗？</p>
                    <p class="faq-a">A: 所有工具完全免费使用，无需支付任何费用。</p>
                </div>
                <div class="faq-item">
                    <p class="faq-q">Q: 我的文件安全吗？</p>
                    <p class="faq-a">A: 所有文件处理都在您的浏览器本地进行，不会上传到服务器，您可以放心使用。</p>
                </div>
                <div class="faq-item">
                    <p class="faq-q">Q: 需要注册账号吗？</p>
                    <p class="faq-a">A: 不需要！所有功能均可直接使用，无需注册登录。</p>
                </div>
                <div class="faq-item">
                    <p class="faq-q">Q: 支持哪些文件格式？</p>
                    <p class="faq-a">A: 各工具支持的格式不同，请在具体工具页面查看详细说明。</p>
                </div>
                <h4>使用指南</h4>
                <ol>
                    <li>选择您需要使用的工具类型</li>
                    <li>上传或输入您的文件/内容</li>
                    <li>根据需要进行设置调整</li>
                    <li>点击处理按钮完成操作</li>
                    <li>下载或复制处理结果</li>
                </ol>
            </div>
        </el-dialog>

        <!-- 用户协议弹窗 -->
        <el-dialog title="用户协议" :visible.sync="showUserAgreement" width="700px">
            <div class="dialog-content agreement-content">
                <p><strong>欢迎使用 FreeToolBox 免费在线工具箱！</strong></p>
                <p>在使用本服务之前，请仔细阅读以下用户协议。使用本工具即表示您同意接受本协议的约束。</p>

                <h4>一、服务说明</h4>
                <p>FreeToolBox 提供多种免费在线工具，包括但不限于文件转换、图片处理、格式校验等功能。我们有权随时修改或中断服务，恕不另行通知。</p>

                <h4>二、使用规则</h4>
                <ul>
                    <li>您同意仅将本服务用于合法目的</li>
                    <li>您不得对本服务进行任何形式的破解、反编译或非法获取</li>
                    <li>您不得上传含有恶意代码或非法内容的文件</li>
                    <li>您不得滥用本服务，包括但不限于大量请求造成服务器负担</li>
                </ul>

                <h4>三、数据安全</h4>
                <p>所有文件处理均在您的浏览器本地进行，数据不会上传至服务器。我们无法访问您的私人文件，但也无法为您保存任何处理结果，请及时下载保存。</p>

                <h4>四、免责声明</h4>
                <ul>
                    <li>本服务按"现状"提供，不提供任何明示或暗示的保证</li>
                    <li>因使用本服务造成的任何直接或间接损失，我们不承担责任</li>
                    <li>用户需自行承担使用本服务的风险</li>
                </ul>

                <h4>五、知识产权</h4>
                <p>本网站上所有工具的源代码和设计版权归 FreeToolBox 所有，未经授权不得复制或传播。</p>

                <h4>六、协议更新</h4>
                <p>我们保留随时修改本协议的权利。修改后的协议一旦公布即生效，如您继续使用服务则视为接受修改后的协议。</p>

                <p class="update-time">最后更新日期：2025年1月</p>
            </div>
        </el-dialog>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                searchKeyword: '',
                activeCategory: 'all',
                showSearchHistory: false,
                showFavorites: false,
                showAboutUs: false,
                showContactUs: false,
                showHelpCenter: false,
                showUserAgreement: false,
                searchHistory: [],
                favorites: [],
                categories: [
                    { name: 'all', label: '全部工具', icon: '🧰' },
                    { name: 'image', label: '图片处理', icon: '🖼️' },
                    { name: 'document', label: '文档转换', icon: '📄' },
                    { name: 'development', label: '开发工具', icon: '💻' },
                    { name: 'soft', label: '资源分享', icon: '📊' }
                ],
                tools: [
                    {
                        name: '图片生成GIF',
                        description: '将多张图片合成GIF动画',
                        icon: '🖼️',
                        color: '#FF6B6B',
                        baseCount: 292,
                        tag: '热门',
                        tagType: 'danger',
                        path: '/ImageToGif',
                        category: 'image',
                        keywords: ['gif', '动图', '动画', '图片合成']
                    },
                    {
                        name: '图片压缩',
                        description: '无损压缩图片，减小文件大小',
                        icon: '🗜️',
                        color: '#4ECDC4',
                        baseCount: 269,
                        tag: '新',
                        tagType: 'success',
                        path: '/ImageCompressor',
                        category: 'image',
                        keywords: ['压缩', '缩小', '优化']
                    },
                    {
                        name: 'Pdf转Word',
                        description: '精准转换PDF为可编辑Word文档',
                        icon: '📄',
                        color: '#45B7D1',
                        baseCount: 703,
                        tag: '推荐',
                        tagType: 'warning',
                        path: '/PdfToWord',
                        category: 'document',
                        keywords: ['pdf', 'word', '转换', '文档']
                    },
                    {
                        name: 'Pdf转图片',
                        description: '精准转换PDF图片',
                        icon: '📄',
                        color: '#34d153',
                        baseCount: 703,
                        tag: '推荐',
                        tagType: 'warning',
                        path: '/PdfToImg',
                        category: 'document',
                        keywords: ['pdf', '图片', '转换', '截图']
                    },
                    {
                        name: 'JSON校验、格式化',
                        description: '校验JSON语法并美化格式',
                        icon: '{}',
                        color: '#96CEB4',
                        baseCount: 521,
                        tag: '开发',
                        tagType: 'info',
                        path: '/JsonFormatter',
                        category: 'development',
                        keywords: ['json', '格式化', '校验', '代码']
                    },
                    {
                        name: 'Excel转JSON',
                        description: '将Excel表格数据转换为JSON格式',
                        icon: '📊',
                        color: '#96CEB4',
                        baseCount: 521,
                        tag: '开发',
                        tagType: 'info',
                        path: '/ExcelToJSON',
                        category: 'development',
                        keywords: ['excel', 'json', '表格', '转换']
                    },
                    {
                        name: '二维码生成',
                        description: '快速生成自定义二维码',
                        icon: '🔲',
                        color: '#FFEAA7',
                        baseCount: 157,
                        tag: '常用',
                        tagType: '',
                        path: '/QrCodeGenerator',
                        category: 'development',
                        keywords: ['二维码', 'qr', '扫码', '链接']
                    },
                    {
                        name: '水印添加',
                        description: '为图片添加文字或图片水印',
                        icon: '💧',
                        color: '#DDA0DD',
                        baseCount: 83,
                        tag: '新',
                        tagType: 'success',
                        path: '/ImageWatermark',
                        category: 'image',
                        keywords: ['水印', '版权', '标记']
                    },
                    {
                        name: '视频转GIF',
                        description: '从视频中截取片段制作GIF',
                        icon: '🎬',
                        color: '#FFB3BA',
                        baseCount: 421,
                        tag: '热门',
                        tagType: 'danger',
                        path: '/VideoToGif',
                        category: 'image',
                        keywords: ['视频', 'gif', '动图', '截取']
                    },
                    {
                        name: '网盘资源分享',
                        description: '分享各类软件、资料、小说、视频等网盘资源',
                        icon: '🔍',
                        color: '#90EE90',
                        baseCount: 16583,
                        tag: '网盘',
                        tagType: 'info',
                        path: '/NetDiskShare',
                        category: 'soft',
                        keywords: ['网盘', '分享', '资源', '下载']
                    },
                    {
                        name: '在线提取MP3',
                        description: '从在线视频中提取音频',
                        icon: '🎵',
                        color: '#90EE90',
                        baseCount: 16583,
                        tag: '工具',
                        tagType: 'info',
                        path: '/AudioExtractor',
                        category: 'development',
                        keywords: ['音频', 'mp3', '提取', '音乐']
                    },
                    /*{
                        name: '问题填报',
                        description: '问题信息填报与管理系统',
                        icon: '📝',
                        color: '#90EE90',
                        baseCount: 16583,
                        tag: '工具',
                        tagType: 'info',
                        path: '/ProblemVerificationForm',
                        category: 'development',
                        keywords: ['填报', '问题', '表单']
                    },
                    {
                        name: '问题核查',
                        description: '问题核查与审核工具',
                        icon: '✅',
                        color: '#90EE90',
                        baseCount: 16583,
                        tag: '工具',
                        tagType: 'info',
                        path: '/InspectionReportForm',
                        category: 'development',
                        keywords: ['核查', '审核', '检查']
                    }*/
                ],
                hotSearchKeywords: ['PDF转Word', '图片压缩', 'GIF', '二维码', 'JSON格式化'],
                siteName: 'FreeToolBox - 免费在线工具箱',
                siteDescription: '提供多种免费在线工具，包括PDF转换、图片处理、格式转换等高效工具',
                structuredData: ''
            };
        },

        computed: {
            displayedTools() {
                let result = this.tools;
                if (this.activeCategory !== 'all') {
                    result = result.filter(tool => tool.category === this.activeCategory);
                }
                if (this.searchKeyword) {
                    const keyword = this.searchKeyword.toLowerCase();
                    result = result.filter(tool =>
                        tool.name.toLowerCase().includes(keyword) ||
                        tool.description.toLowerCase().includes(keyword) ||
                        (tool.keywords && tool.keywords.some(k => k.toLowerCase().includes(keyword)))
                    );
                }
                return result;
            }
        },

        watch: {
            searchKeyword(val) {
                this.updatePageTitle(val);
            }
        },

        methods: {
            // ========== 搜索相关方法 ==========
            querySearch(queryString, callback) {
                if (!queryString) { callback([]); return; }
                const keyword = queryString.toLowerCase();
                const results = this.tools
                    .filter(tool =>
                        tool.name.toLowerCase().includes(keyword) ||
                        tool.description.toLowerCase().includes(keyword) ||
                        (tool.keywords && tool.keywords.some(k => k.toLowerCase().includes(keyword)))
                    )
                    .slice(0, 8)
                    .map(tool => ({ ...tool, value: tool.name }));
                callback(results);
            },

            handleSearchSelect(item) {
                this.addToHistory(this.searchKeyword);
                this.handleToolClick(item);
            },

            handleSearchChange() {
                if (this.searchKeyword) this.addToHistory(this.searchKeyword);
            },

            searchFromHistory(keyword) {
                this.searchKeyword = keyword;
                this.showSearchHistory = false;
            },

            searchFromHot(keyword) {
                this.searchKeyword = keyword;
            },

            addToHistory(keyword) {
                if (!keyword) return;
                this.searchHistory = this.searchHistory.filter(h => h !== keyword);
                this.searchHistory.unshift(keyword);
                this.searchHistory = this.searchHistory.slice(0, 10);
                this.saveHistory();
            },

            clearHistory() {
                this.searchHistory = [];
                localStorage.removeItem('toolSearchHistory');
            },

            saveHistory() {
                localStorage.setItem('toolSearchHistory', JSON.stringify(this.searchHistory));
            },

            clearSearch() {
                this.searchKeyword = '';
            },

            // ========== 收藏相关方法 ==========
            toggleFavorite(tool) {
                const index = this.favorites.findIndex(f => f.name === tool.name);
                if (index > -1) {
                    this.favorites.splice(index, 1);
                    this.$message.success('已取消收藏');
                } else {
                    this.favorites.push(tool);
                    this.$message.success('已添加到收藏');
                }
                this.saveFavorites();
            },

            isFavorited(tool) {
                return this.favorites.some(f => f.name === tool.name);
            },

            removeFavorite(tool) {
                this.toggleFavorite(tool);
            },

            saveFavorites() {
                localStorage.setItem('toolFavorites', JSON.stringify(this.favorites));
            },

            loadFavorites() {
                const saved = localStorage.getItem('toolFavorites');
                if (saved) {
                    try { this.favorites = JSON.parse(saved); }
                    catch (e) { this.favorites = []; }
                }
            },

            // ========== 工具点击处理 ==========
            handleToolClick(tool) {
                tool.baseCount += 1;
                this.saveToolsToLocalStorage();
                this.addToHistory(tool.name);
                window.open(tool.path, '_blank');
                this.trackUsage(tool);
            },

            handleCategoryClick(category) {
                this.activeCategory = category;
                if (this.searchKeyword) this.clearSearch();
            },

            scrollToTools() {
                document.getElementById('tools-section')?.scrollIntoView({ behavior: 'smooth' });
            },

            // ========== 本地存储 ==========
            saveToolsToLocalStorage() {
                localStorage.setItem('toolsData', JSON.stringify(this.tools));
            },

            loadToolsFromLocalStorage() {
                const saved = localStorage.getItem('toolsData');
                if (saved) {
                    try {
                        const savedTools = JSON.parse(saved);
                        savedTools.forEach(savedTool => {
                            const tool = this.tools.find(t => t.name === savedTool.name);
                            if (tool) tool.baseCount = savedTool.baseCount;
                        });
                    } catch (e) { console.error('加载工具数据失败:', e); }
                }
            },

            trackUsage(tool) {
                const stats = JSON.parse(localStorage.getItem('toolUsageStats') || '{}');
                stats[tool.name] = (stats[tool.name] || 0) + 1;
                stats.total = (stats.total || 0) + 1;
                localStorage.setItem('toolUsageStats', JSON.stringify(stats));
            },

            // ========== SEO优化 ==========
            updatePageTitle(keyword) {
                document.title = keyword ? `${keyword} - ${this.siteName} | 文本 / 图片 / 开发实用工具` : `${this.siteName} | 文本 / 图片 / 开发实用工具`;
            },

            generateStructuredData() {
                this.structuredData = JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: this.siteName,
                    description: this.siteDescription,
                    url: window.location.origin,
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: { '@type': 'EntryPoint', urlTemplate: window.location.origin + '?search={search_term_string}' },
                        'query-input': 'required name=search_term_string'
                    }
                });
            },

            updateMetaTags() {
                let metaDesc = document.querySelector('meta[name="description"]');
                if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
                metaDesc.content = this.siteDescription;

                let metaKeywords = document.querySelector('meta[name="keywords"]');
                if (!metaKeywords) { metaKeywords = document.createElement('meta'); metaKeywords.name = 'keywords'; document.head.appendChild(metaKeywords); }
                metaKeywords.content = '在线工具,免费工具,PDF转换,图片处理,格式转换,GIF生成,JSON格式化';

                this.updateOpenGraph();
            },

            updateOpenGraph() {
                const ogTags = [
                    { property: 'og:title', content: this.siteName },
                    { property: 'og:description', content: this.siteDescription },
                    { property: 'og:type', content: 'website' },
                    { property: 'og:url', content: window.location.href },
                    { property: 'og:site_name', content: this.siteName }
                ];
                ogTags.forEach(tag => {
                    let el = document.querySelector(`meta[property="${tag.property}"]`);
                    if (!el) { el = document.createElement('meta'); el.setAttribute('property', tag.property); document.head.appendChild(el); }
                    el.content = tag.content;
                });
            },

            formatCount(count) { return count; },

            showLoginAlert() {
                this.$message.warning('不用登录，直接用！');
            },

            goHome() {
                this.activeCategory = 'all';
                this.searchKeyword = '';
                this.showFavorites = false;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },

        mounted() {
            this.loadToolsFromLocalStorage();
            const savedHistory = localStorage.getItem('toolSearchHistory');
            if (savedHistory) { try { this.searchHistory = JSON.parse(savedHistory); } catch (e) { this.searchHistory = []; } }
            this.loadFavorites();
            this.generateStructuredData();
            this.updateMetaTags();
            this.updatePageTitle();
            this.$nextTick(() => {
                const searchInput = document.querySelector('.search-input input');
                if (searchInput) {
                    searchInput.addEventListener('focus', () => { this.showSearchHistory = true; });
                    searchInput.addEventListener('blur', () => { setTimeout(() => { this.showSearchHistory = false; }, 200); });
                }
            });
        }
    };
</script>

<style scoped>
    .onboarding-page {
        min-height: 100vh;
        background-color: #f5f7fa;
    }

    .top-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 15px 5%;
        background-color: white;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        position: sticky;
        top: 0;
        z-index: 100;
    }

    .logo {
        font-size: 24px;
        font-weight: bold;
        color: #4775d8;
        cursor: pointer;
        display: flex;
        align-items: center;
    }

    .logo-icon {
        margin-right: 8px;
    }

    .search-container {
        position: relative;
        flex: 1;
        max-width: 500px;
        margin: 0 20px;
    }

    .search-input {
        width: 100%;
    }

    .search-history {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        padding: 12px;
        margin-top: 5px;
        z-index: 1000;
    }

    .history-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        font-size: 14px;
        color: #666;
    }

    .history-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .history-item {
        cursor: pointer;
    }

    .favorite-badge {
        margin-right: 10px;
    }

    .favorites-bar {
        background: white;
        padding: 15px 5%;
        border-bottom: 1px solid #eee;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .favorites-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        font-weight: 600;
        color: #333;
    }

    .favorites-list {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
    }

    .favorite-item {
        display: flex;
        align-items: center;
        padding: 8px 15px;
        background: #f5f7fa;
        border-radius: 20px;
        cursor: pointer;
        transition: all 0.3s;
        position: relative;
    }

    .favorite-item:hover {
        background: #e8f4ff;
        transform: translateY(-2px);
    }

    .favorite-icon {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        margin-right: 8px;
    }

    .favorite-name {
        font-size: 14px;
        color: #333;
    }

    .remove-favorite {
        margin-left: 10px;
        color: #999;
        font-size: 12px;
    }

    .remove-favorite:hover {
        color: #f56c6c;
    }

    .favorite-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        opacity: 0;
    }

    .tool-card:hover .favorite-btn {
        opacity: 1;
    }

    .favorite-btn:hover {
        background: #fff;
        transform: scale(1.1);
    }

    .favorite-btn.is-favorited {
        color: #f56c6c;
        opacity: 1 !important;
    }

    .favorite-btn i {
        font-size: 18px;
    }

    .search-suggestion {
        display: flex;
        align-items: center;
    }

    .suggestion-name {
        margin-left: 10px;
        flex: 1;
    }

    .suggestion-tag {
        font-size: 12px;
        color: #999;
    }

    .hot-search-bar {
        background: white;
        padding: 15px 20px;
        border-radius: 10px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
    }

    .hot-label {
        font-weight: 600;
        color: #333;
    }

    .hot-tag {
        cursor: pointer;
    }

    .search-result-tip {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        background: #e8f4ff;
        border-radius: 10px;
        margin-bottom: 20px;
        color: #4775d8;
    }

    .login-button {
        margin-left: 20px;
    }

    .main-content {
        max-width: 1200px;
        margin: 30px auto;
        padding: 0 20px;
    }

    .welcome-section {
        text-align: center;
        padding: 50px 0;
        background: linear-gradient(120deg, #4775d8, #6a8cff);
        border-radius: 15px;
        color: white;
        margin-bottom: 30px;
    }

    .welcome-title {
        font-size: 32px;
        margin-bottom: 15px;
    }

    .welcome-subtitle {
        font-size: 18px;
        margin-bottom: 30px;
        opacity: 0.9;
    }

    .explore-button {
        font-size: 16px;
        padding: 12px 30px;
    }

    .categories {
        margin-bottom: 30px;
    }

    .category-tabs {
        display: flex;
        background: white;
        border-radius: 50px;
        padding: 5px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .category-tab {
        flex: 1;
        text-align: center;
        padding: 12px 20px;
        cursor: pointer;
        border-radius: 50px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 500;
    }

    .category-tab:hover {
        background-color: #f0f5ff;
    }

    .category-tab.active {
        background: linear-gradient(90deg, #4775d8, #6a8cff);
        color: white;
        box-shadow: 0 4px 12px rgba(71, 117, 216, 0.3);
    }

    .tab-icon {
        margin-right: 8px;
        font-size: 18px;
    }

    .tools-grid {
        margin-bottom: 40px;
    }

    .tool-card {
        cursor: pointer;
        transition: all 0.3s ease;
        border-radius: 10px;
        overflow: hidden;
        position: relative;
    }

    .tool-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }

    .tool-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px 0;
        position: relative;
    }

    .icon-placeholder {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 30px;
    }

    .tool-info {
        padding: 0 15px 20px;
        text-align: center;
    }

    .tool-name {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    .tool-description {
        font-size: 14px;
        color: #666;
        margin-bottom: 15px;
        height: 40px;
        overflow: hidden;
    }

    .tool-stats {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .use-count {
        font-size: 12px;
        color: #999;
    }

    .tools-grid .el-col {
        margin-bottom: 20px;
    }

    .empty-favorites {
        padding: 40px 0;
        text-align: center;
    }

    .footer {
        background-color: #333;
        color: #fff;
        padding: 10px 0;
        margin-top: 50px;
    }

    .footer-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 0 10px;
        text-align: center;
    }

    .footer-links {
        margin-bottom: 10px;
    }

    .footer-links a {
        color: #fff;
        margin: 0 15px;
        text-decoration: none;
        cursor: pointer;
    }

    .footer-links a:hover {
        text-decoration: underline;
        color: #6a8cff;
    }

    .footer-gongyi {
        margin: 10px 0;
        color: #fff;
        font-size: 13px;
    }

    .footer-gongyi a {
        color: #fff;
        opacity: 0.8;
        margin: 0 5px;
        text-decoration: none;
    }

    .footer-gongyi a:hover {
        text-decoration: underline;
        opacity: 1;
    }

    .copyright {
        font-size: 14px;
        opacity: 0.7;
        margin: 0;
    }

    .beian {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 16px;
        font-size: 12px;
        margin-top: 10px;
    }

    .beian-link {
        color: #666;
        text-decoration: none;
    }

    .beian-link:hover {
        color: #4775d8;
        text-decoration: underline;
    }

    /* 响应式设计 */
    @media (max-width: 768px) {
        .top-nav {
            flex-direction: column;
            padding: 15px;
        }

        .logo {
            margin-bottom: 15px;
        }

        .search-container {
            width: 100%;
            margin: 0 0 15px 0;
            max-width: none;
        }

        .login-button {
            margin: 0;
        }

        .welcome-title {
            font-size: 24px;
        }

        .category-tabs {
            flex-wrap: wrap;
        }

        .category-tab {
            flex: 0 0 50%;
            margin-bottom: 5px;
        }

        .el-col {
            width: 50%;
            margin-bottom: 20px;
        }

        .footer-content {
            padding: 0 16px;
        }

        .beian {
            gap: 8px;
        }

        .hot-search-bar {
            padding: 12px;
        }

        .favorites-list {
            gap: 10px;
        }

        .favorite-item {
            padding: 6px 12px;
        }
    }

    /* 弹窗样式 */
    .dialog-content {
        line-height: 1.8;
    }

    .dialog-content h3 {
        color: #4775d8;
        margin-bottom: 15px;
    }

    .dialog-content h4 {
        color: #333;
        margin-top: 20px;
        margin-bottom: 10px;
    }

    .dialog-content ul, .dialog-content ol {
        padding-left: 20px;
        margin: 10px 0;
    }

    .dialog-content li {
        margin-bottom: 5px;
    }

    .contact-methods {
        margin: 20px 0;
    }

    .contact-item {
        display: flex;
        align-items: center;
        margin-bottom: 15px;
        font-size: 15px;
    }

    .contact-item i {
        margin-right: 10px;
        font-size: 18px;
        color: #4775d8;
    }

    .contact-tip {
        color: #666;
        font-size: 13px;
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px dashed #eee;
    }

    .faq-item {
        margin-bottom: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid #f0f0f0;
    }

    .faq-q {
        color: #333;
        font-weight: 500;
        margin-bottom: 5px;
    }

    .faq-a {
        color: #666;
        margin: 0;
    }

    .agreement-content h4 {
        font-size: 15px;
        color: #4775d8;
        margin-top: 20px;
    }

    .agreement-content p {
        text-align: justify;
    }

    .update-time {
        color: #999;
        font-size: 12px;
        text-align: right;
        margin-top: 20px;
    }
</style>
